import React, { useState } from 'react';
import { Box, FormControl, MenuItem, Select, Button, Typography, Paper, useMediaQuery, useTheme } from '@mui/material';
import { downloadFilesAsync } from '../../services/FileDownloader';
import { userHasAccess } from '../../services/RoleBasedAccess';
import { useAssetsContext } from '../../context/AssetsContext';
import { useSession } from '../../context/SessionContext';
import { useAssetActions } from '../../hooks/asset/useAssetActions';
import { Asset } from '../../state/types';

const formatOperationLabel = (operation: string) => {
  switch (operation) {
    case 'archive':
      return 'archive';
    case 'unarchive':
      return 'unarchive';
    case 'syndication':
      return 'send to syndication';
    case 'removeRedrive':
      return 'remove from redrive for';
    case 'thumbnails':
      return 'regenerate thumbnails for';
    case 'exterior_bgr':
      return 'reprocess background for';
    case 'cropping':
      return 'reprocess cropped images for';
    case 'featured':
      return 'reprocess featured images for';
    default:
      return `trigger ${operation} for`;
  }
};

const buildBatchResultMessage = (
  operation: string,
  counts: { started: number; locked: number; failed: number; duplicate: number }
) => {
  const action = formatOperationLabel(operation);
  const parts: string[] = [];

  if (counts.started > 0) {
    parts.push(`${counts.started} ${counts.started === 1 ? 'asset was' : 'assets were'} queued to ${action}.`);
  }
  if (counts.locked > 0) {
    parts.push(`${counts.locked} ${counts.locked === 1 ? 'asset is' : 'assets are'} currently locked.`);
  }
  if (counts.failed > 0) {
    parts.push(`${counts.failed} ${counts.failed === 1 ? 'asset failed' : 'assets failed'} to start.`);
  }
  if (counts.duplicate > 0) {
    parts.push(`${counts.duplicate} duplicate ${counts.duplicate === 1 ? 'request was' : 'requests were'} skipped.`);
  }

  return parts.join(' ');
};

const normalizeSyndicationName = (value?: string) =>
  (value || '').replace(/[^a-z0-9]/gi, '').toLowerCase();

const getAssetRedriveType = (asset: Asset): 'crs' | 'mashery' | null => {
  const candidates = [
    asset.syndicationCurrentStep,
    asset.syndicationSteps?.[0],
    ...((asset as Asset & Record<string, unknown>).syndicationSteps || []),
  ]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .map(normalizeSyndicationName);

  if (candidates.some((value) => value.includes('mashery'))) {
    return 'mashery';
  }

  if (candidates.some((value) => value.includes('crs'))) {
    return 'crs';
  }

  return null;
};

const AssetOperations: React.FC = () => {
  const { selectedAssets, setSelectedAssets, refetch } = useAssetsContext();
  const { user } = useSession();
  const { reprocessAsset, secondaryProcessAsset, removeAssetRedrive } = useAssetActions();
  const [selectedOperation, setSelectedOperation] = useState('');
  const role = user?.role || '';

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const onAssetsUpdate = () => {
    setSelectedAssets([]);
    refetch();
  };

  const executeOperation = async () => {
    if (!selectedOperation) return;

    try {
      if (selectedOperation === 'downloadImages') {
        selectedAssets.forEach((asset) => downloadFilesAsync(asset));
      } else if (selectedOperation === 'secondaryProcess') {
        selectedAssets.forEach((asset) => {
          secondaryProcessAsset(asset.pk, true);
        });
        alert(`Triggered secondary processing for ${selectedAssets.length} assets.`);
      } else if (selectedOperation === 'removeRedrive') {
        const eligibleAssets = selectedAssets
          .map((asset) => ({ asset, redriveType: getAssetRedriveType(asset) }))
          .filter(
            (entry): entry is { asset: Asset; redriveType: 'crs' | 'mashery' } =>
              entry.redriveType !== null
          );

        const skippedCount = selectedAssets.length - eligibleAssets.length;
        const results = await Promise.all(
          eligibleAssets.map(({ asset, redriveType }) =>
            removeAssetRedrive(asset.pk, redriveType, asset.sk, true)
          )
        );

        const startedCount = results.filter((result) => result.status === 'started').length;
        const duplicateCount = results.filter((result) => result.status === 'duplicate').length;
        const failedCount = results.filter((result) => result.status === 'failed').length;

        const messageParts: string[] = [];
        if (startedCount > 0) {
          messageParts.push(
            `${startedCount} ${startedCount === 1 ? 'asset was' : 'assets were'} removed from redrive.`
          );
        }
        if (failedCount > 0) {
          messageParts.push(
            `${failedCount} ${failedCount === 1 ? 'asset failed' : 'assets failed'} to remove from redrive.`
          );
        }
        if (duplicateCount > 0) {
          messageParts.push(
            `${duplicateCount} duplicate ${duplicateCount === 1 ? 'request was' : 'requests were'} skipped.`
          );
        }
        if (skippedCount > 0) {
          messageParts.push(
            `${skippedCount} ${skippedCount === 1 ? 'asset was' : 'assets were'} skipped because the syndicator is not CRS or Mashery.`
          );
        }

        refetch();
        alert(messageParts.join(' ') || 'No redrive removals were sent.');
      } else {
        const results = await Promise.all(
          selectedAssets.map((asset) =>
            reprocessAsset(asset.pk, asset.sk, selectedOperation, { notify: false })
          )
        );

        const counts = results.reduce(
          (acc, result) => {
            acc[result.status] += 1;
            return acc;
          },
          { started: 0, locked: 0, failed: 0, duplicate: 0 }
        );

        if (selectedOperation === 'archive' || selectedOperation === 'unarchive') {
          onAssetsUpdate();
        }

        const message = buildBatchResultMessage(selectedOperation, counts);
        if (message) {
          alert(message);
        } else {
          alert(`No ${selectedOperation} requests were sent.`);
        }
      }
    } finally {
      setSelectedAssets([]);
      setSelectedOperation('');
    }
  };

  if (selectedAssets.length === 0) return null;

  return (
    <Paper sx={{ padding: 2, borderRadius: 2, marginBottom: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Asset Operations
        </Typography>
        <Button
          data-cy="asset-ops-clear-btn"
          variant="outlined"
          size="small"
          onClick={() => setSelectedAssets([])}
          sx={{
            textTransform: 'none',
            color: theme.palette.text.secondary,
            borderColor: theme.palette.divider,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              borderColor: theme.palette.text.secondary,
            }
          }}
        >
          Clear ({selectedAssets.length})
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1.5 : 0 }}>
        <FormControl sx={{ width: isMobile ? '100%' : '50%', mr: isMobile ? 0 : 2 }}>
          <Select
            data-cy="asset-ops-operation-select"
            value={selectedOperation}
            onChange={(e) => setSelectedOperation(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="" disabled>Select an Operation...</MenuItem>
            <MenuItem value="downloadImages">Download Images</MenuItem>
            {userHasAccess(role, ['Internal']) && [
              <MenuItem value="cropping" key="cropping">Reprocess Cropped</MenuItem>,
              <MenuItem value="featured" key="featured">Reprocess Featured</MenuItem>,
              <MenuItem value="exterior_bgr" key="exterior_bgr">Reprocess Background</MenuItem>,
              <MenuItem value="thumbnails" key="thumbnails">Regenerate Thumbnails</MenuItem>
            ]}
            {userHasAccess(role, ['Internal', 'Client Plus']) && (
              <MenuItem value="secondaryProcess" key="secondaryProcess">Secondary Process</MenuItem>
            )}
            {userHasAccess(role, ['Internal', 'Client Plus']) && (
              <MenuItem value="syndication">Send to Syndication</MenuItem>
            )}
            {userHasAccess(role, ['Internal']) && (
              <MenuItem value="removeRedrive">Remove from Redrive</MenuItem>
            )}
            {userHasAccess(role, ['Internal']) && (
              <MenuItem value="archive" key="archive">Archive</MenuItem>
            )}
            {selectedAssets.some((asset) => !!asset.archived) && userHasAccess(role, ['Internal']) && (
              <MenuItem value="unarchive">Unarchive</MenuItem>
            )}
          </Select>
        </FormControl>

        <Button
          data-cy="asset-ops-execute-btn"
          variant="contained"
          color="warning"
          onClick={executeOperation}
          disabled={!selectedOperation}
          sx={{ width: isMobile ? '100%' : 'auto' }}
        >
          Execute
        </Button>
      </Box>
    </Paper>
  );
};

export default AssetOperations;
